import React, { FC, useCallback, useState } from "react";
import { ButtonColor } from "enums/Button";
import { IconType } from "enums/Icon";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { Currency } from "styles/parcelsStyles";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import Checkbox from "../../../../components/Checkbox/Checkbox";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import { updateRequireInsurance } from "../../../../reduxFolder/reducers/outgoing";
import {
  authCreditCardFeePercent,
  authOverpayFeePercent,
  authPaypalFeePercent,
  authSelectUnits,
  authStripeFeePercent,
} from "../../../../reduxFolder/selectors/globalSelectors";
import { outgoingSelectLoadingInsurance } from "../../../../reduxFolder/selectors/outgoingSelectors";
import formatter from "../../../../utils/formatter";
import {
  calculatePaymentFee,
  getPaymentFeePercent,
} from "../../../../utils/paymentFees";
import type { PaymentMethod } from "../../../../utils/paymentMethods";
import {
  DashConnector,
  InsuranceButton,
  InsuranceCheckBoxContainer,
  InsuranceConfirmation,
  InsuranceHelpIcon,
  InsurancePriceWrapper,
  PriceItem,
  PriceWrapper,
  Separator,
  SummaryCurrency,
  SummaryHelpIcon,
  SummaryLabel,
  SummaryPriceItem,
  SummaryPriceWrapper,
  SummaryTooltipContent,
  TooltipInfo,
  TotalPriceItem,
  Wrapper,
} from "./PriceList.styles";

interface PriceListProps {
  selectedPaymentMethod?: PaymentMethod;
  units: string;
  parcel: any;
  payFromBalance?: boolean;
  isLoadingUpdateInsurance?: boolean;
  updateRequireInsurance: (id: string, data: any) => Promise<any>;
  authCreditCardFeePercent: string;
  authOverpayFeePercent: string;
  authPaypalFeePercent: string;
  authStripeFeePercent: string;
}

type SummaryPriceTone = "default" | "positive" | "negative";

const PriceList: FC<PriceListProps> = React.memo(
  ({
    selectedPaymentMethod,
    units,
    authCreditCardFeePercent,
    authOverpayFeePercent,
    authPaypalFeePercent,
    authStripeFeePercent,
    parcel,
    payFromBalance = false,
    isLoadingUpdateInsurance = false,
    updateRequireInsurance,
  }) => {
    const { t } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();
    const deliveryName = parcel.get("preferred_carrier");
    const deliveryCost = parcel.getIn(["estimate", "price"], 0);
    const packingCost = parcel.getIn(["estimate", "optional_lines_cost"], 0);
    const addonsCost = parcel.getIn(["estimate", "items_addons_cost"], 0);
    const serviceRequestsCost = parcel.getIn(
      ["estimate", "service_requests_cost"],
      0,
    );
    const totalCosts = parcel.getIn(["estimate", "total"], 0);
    const userBalance = parcel.getIn(["estimate", "user_balance"], 0);
    const totalToPay = parcel.getIn(["estimate", "user_total"], 0);
    const state = parcel.get("state");
    const isPaid = state === "paid" || state === "shipped";

    const userBalanceToShow = Math.min(userBalance, totalCosts);
    let totalToPayToShow = Math.max(totalToPay, 0);

    const paymentFeePercent = payFromBalance
      ? 0
      : getPaymentFeePercent(selectedPaymentMethod, {
          creditCard: authCreditCardFeePercent,
          overpay: authOverpayFeePercent,
          paypal: authPaypalFeePercent,
          stripe: authStripeFeePercent,
        });
    // Label in format 5% to show
    const commissionFeeValueLabel = paymentFeePercent * 100;
    const commissionFeeValue = calculatePaymentFee(
      totalToPayToShow,
      paymentFeePercent,
    );
    totalToPayToShow = formatter.roundTo2Decimal(
      totalToPayToShow + commissionFeeValue,
    );

    const hasPackingCosts = packingCost > 0;
    const hasAddonsCosts = addonsCost > 0;
    const hasServiceRequestsCost = serviceRequestsCost > 0;
    const shouldShowUserBalance = userBalance !== 0;

    const weight =
      parcel.get("weight") && Number(parcel.get("weight")).toFixed(2);
    const isRequireInsurance = parcel.getIn(
      ["estimate", "require_insurance"],
      false,
    );
    const [showInsuranceConfirmation, setShowInsuranceConfirmation] =
      useState(false);
    const isEditDisabled = isPaid || showInsuranceConfirmation;

    const onChangeInsurance = () => {
      if (isRequireInsurance === true) {
        updateRequireInsurance(parcel.get("id"), {
          require_insurance: false,
        });
      } else {
        setShowInsuranceConfirmation(true);
      }
    };

    const handleInsuranceCancel = useCallback(
      () => setShowInsuranceConfirmation(false),
      [],
    );

    const handleInsuranceConfirm = useCallback(() => {
      updateRequireInsurance(parcel.get("id"), {
        require_insurance: true,
      }).then(() => setShowInsuranceConfirmation(false));
    }, []);

    const renderPriceItem = (title: string, amount: number) => {
      return (
        <PriceItem>
          {title}
          <DashConnector />
          <PriceWrapper>
            <Currency>{currencySymbol}</Currency>
            {amount}
          </PriceWrapper>
        </PriceItem>
      );
    };

    const renderSummaryPriceItem = (
      title: React.ReactNode,
      amount: number,
      tone: SummaryPriceTone = "default",
    ) => {
      const currencyPrefix =
        tone === "positive"
          ? `+${currencySymbol}`
          : tone === "negative"
            ? `-${currencySymbol}`
            : currencySymbol;

      return (
        <SummaryPriceItem>
          {title}
          <SummaryPriceWrapper $tone={tone}>
            <SummaryCurrency>{currencyPrefix}</SummaryCurrency>
            {Math.abs(amount)}
          </SummaryPriceWrapper>
        </SummaryPriceItem>
      );
    };

    return (
      <Wrapper>
        <PriceItem>
          {`${deliveryName} / ${weight} ${t(`units.${units}.primaryShort`)}`}
          <DashConnector />
          <PriceWrapper>
            <Currency>{currencySymbol}</Currency>
            {deliveryCost}
          </PriceWrapper>
        </PriceItem>
        <PriceItem>
          <InsuranceCheckBoxContainer>
            <Checkbox
              label={t("parcels.insurance")}
              checked={isRequireInsurance}
              disabled={isEditDisabled}
              isLoading={isLoadingUpdateInsurance}
              onChange={onChangeInsurance}
            />

            <Tooltip
              body={<TooltipInfo>{t("parcels.insuranceMessage")}</TooltipInfo>}
            >
              <InsuranceHelpIcon type={IconType.QuestionLight} />
            </Tooltip>
          </InsuranceCheckBoxContainer>
          {showInsuranceConfirmation && (
            <InsuranceConfirmation>
              <InsuranceButton onClick={handleInsuranceCancel}>
                {t("common.no")}
              </InsuranceButton>
              <InsuranceButton
                color={ButtonColor.Primary}
                isLoading={isLoadingUpdateInsurance}
                onClick={handleInsuranceConfirm}
              >
                {t("common.yes")}
              </InsuranceButton>
            </InsuranceConfirmation>
          )}
          <InsurancePriceWrapper>
            <Currency>{currencySymbol}</Currency>
            {parcel.getIn(["estimate", "insurance"], 0)}
          </InsurancePriceWrapper>
        </PriceItem>
        {hasPackingCosts &&
          renderPriceItem(t("parcels.packingGrade"), packingCost)}
        {hasServiceRequestsCost &&
          renderPriceItem(
            t("parcels.packingServiceRequests"),
            serviceRequestsCost,
          )}
        {hasAddonsCosts &&
          renderPriceItem(t("parcels.packingOptions"), addonsCost)}

        <Separator />

        {shouldShowUserBalance &&
          renderSummaryPriceItem(
            t("parcels.totalLabel", { amount: totalCosts }),
            totalCosts,
          )}
        {!isPaid && (
          <>
            {shouldShowUserBalance &&
              renderSummaryPriceItem(
                <SummaryLabel>
                  {t("parcels.balance")}
                  <Tooltip
                    body={
                      <SummaryTooltipContent>
                        {userBalance > 0
                          ? t("parcels.positiveBalanceTooltip")
                          : t("parcels.negativeBalanceTooltip")}
                      </SummaryTooltipContent>
                    }
                    place="left"
                  >
                    <SummaryHelpIcon type={IconType.QuestionLight} />
                  </Tooltip>
                </SummaryLabel>,
                userBalanceToShow,
                userBalanceToShow > 0
                  ? "positive"
                  : userBalanceToShow < 0
                    ? "negative"
                    : "default",
              )}
            {commissionFeeValue > 0 &&
              renderSummaryPriceItem(
                t("parcels.commissionFeeLabel", {
                  fee: commissionFeeValueLabel,
                }),
                commissionFeeValue,
              )}
            <TotalPriceItem>
              {t("parcels.totalToPay")}
              <DashConnector />
              <PriceWrapper>
                <Currency>{currencySymbol}</Currency>
                {totalToPayToShow}
              </PriceWrapper>
            </TotalPriceItem>
          </>
        )}
      </Wrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    units: authSelectUnits,
    authCreditCardFeePercent,
    authOverpayFeePercent,
    authPaypalFeePercent,
    authStripeFeePercent,
    isLoadingUpdateInsurance: outgoingSelectLoadingInsurance,
  }),
  {
    updateRequireInsurance,
  },
);

export default withConnect(PriceList);
